import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, Monitor, Zap, CheckCircle, ArrowRight } from "lucide-react";

const SubscriptionPlan2 = () => {
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1); // Number of 10 taka increments
  const pricePerIncrement = 10; // 10 taka
  const minutesPerIncrement = 30; // 30 minutes per 10 taka
  const totalPrice = quantity * pricePerIncrement;
  const totalMinutes = quantity * minutesPerIncrement;

  const handlePayment = () => {
    navigate('/payment', { state: { amount: totalPrice } });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full mb-6">
            <Monitor className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Upskill Compute Credit
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            কম্পিউটার সেশনের জন্য প্রয়োজনীয় ক্রেডিট কিনুন। শুধুমাত্র প্রয়োজন অনুযায়ী ব্যবহার করুন।
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
          {/* Pricing Hero */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-8 py-12 text-center text-white">
            <div className="flex items-center justify-center mb-4">
              <Zap className="w-8 h-8 mr-3" />
              <span className="text-2xl font-bold">সরল মূল্য</span>
            </div>
            <div className="text-6xl font-bold mb-2">{pricePerIncrement}৳</div>
            <div className="text-xl opacity-90">প্রতি {minutesPerIncrement} মিনিট কম্পিউটার সেশন</div>
            <div className="mt-6 inline-block bg-white/20 backdrop-blur-sm rounded-full px-6 py-2">
              <span className="text-lg">💡 {pricePerIncrement}৳ = {minutesPerIncrement} মিনিট</span>
            </div>
          </div>

          <div className="p-8">
            {/* Quantity Selector */}
            <div className="mb-8 bg-gray-50 rounded-xl p-6">
              <label className="block text-lg font-semibold text-gray-900 mb-4">
                ক্রেডিট পরিমাণ নির্বাচন করুন
              </label>
              <div className="flex items-center justify-center space-x-4">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-12 h-12 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-bold text-xl"
                  disabled={quantity <= 1}
                >
                  −
                </button>
                <div className="text-center min-w-[200px]">
                  <div className="text-3xl font-bold text-gray-900">{quantity}</div>
                  <div className="text-lg text-gray-600 mt-2">
                    <span className="text-base">{quantity} × {pricePerIncrement}৳ = </span>
                    <span className="text-3xl font-bold text-purple-600">{totalPrice}৳</span>
                  </div>
                  <div className="text-sm text-purple-600 font-semibold mt-2">
                    {totalMinutes} মিনিট কম্পিউটার সেশন
                  </div>
                </div>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-12 h-12 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-bold text-xl"
                >
                  +
                </button>
              </div>
            </div>

            {/* Features Grid */}
            <div className="grid md:grid-cols-2 gap-8 mb-10">
              {/* Pricing Details */}
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-gray-900 flex items-center">
                  <CheckCircle className="w-6 h-6 text-green-500 mr-3" />
                  আপনি যা পাবেন
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">
                      <span className="font-semibold">{minutesPerIncrement} মিনিট</span>{" "}
                      কম্পিউটার সেশন প্রতি {pricePerIncrement}৳
                    </p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">
                      <span className="font-semibold">১০ টাকা বৃদ্ধি</span>{" "}
                      - ১০, ২০, ৩০, ৪০ টাকা ইত্যাদি পরিমাণে ক্রেডিট কিনুন
                    </p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">
                      <span className="font-semibold">নমনীয় স্কেলিং</span> -
                      প্রয়োজন অনুযায়ী পরিমাণ বাড়িয়ে নিন
                    </p>
                  </div>
                </div>
              </div>

              {/* Payment Process */}
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-gray-900 flex items-center">
                  <CreditCard className="w-6 h-6 text-blue-500 mr-3" />
                  কিভাবে কাজ করে
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                      1
                    </div>
                    <p className="text-gray-700">
                      আপনার প্রয়োজনীয় পরিমাণ ক্রেডিট নির্বাচন করুন
                    </p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                      2
                    </div>
                    <p className="text-gray-700">
                      নিরাপদ পেমেন্ট সম্পন্ন করুন
                    </p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                      3
                    </div>
                    <p className="text-gray-700">
                      আপনার ড্যাশবোর্ড রিলোড করুন এবং আপডেট করা ক্রেডিট দেখুন
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <div className="text-center">
              <button
                onClick={handlePayment}
                className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold text-lg rounded-full hover:from-purple-700 hover:to-blue-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <CreditCard className="w-5 h-5 mr-3" />
                {totalPrice}৳ এর জন্য ক্রেডিট কিনুন ({totalMinutes} মিনিট)
                <ArrowRight className="w-5 h-5 ml-3" />
              </button>
              <p className="text-sm text-gray-500 mt-4">
                🔒 নিরাপদ পেমেন্ট
              </p>
            </div>
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center space-x-2 text-gray-600 bg-white/60 backdrop-blur-sm rounded-full px-6 py-3">
            <Zap className="w-4 h-4 text-yellow-500" />
            <span className="text-sm">
              ক্রেডিট কখনো শেষ হয় না • তাৎক্ষণিক সক্রিয়করণ • ২৪/৭ সহায়তা
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlan2;
